/* Мобильная оболочка кабинета (≤ 760 px) — по прототипу (mvp/assets/mobile.js, MOBILE-CHANGELOG.md).
   Вид — styles/mobile.css без изменений; здесь — то, что в прототипе строил скрипт:
   нижняя панель и шторка «Ещё», «назад» в шапке, правые панели шторкой снизу,
   строка сортировки вместо шапки таблицы, прочерки в карточках, умная шапка календаря,
   блокировка автофокуса полей поиска. На десктопе элементы есть в DOM, но скрыты CSS. */
import { useEffect, useLayoutEffect, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ICON, type IconName } from '../ui/icons';

const QUERY = '(max-width:760px)';
const mq = () => (typeof matchMedia === 'function' ? matchMedia(QUERY) : null);
export const isMobile = () => !!mq()?.matches;
/** Телефон ли сейчас (следит за поворотом и ресайзом) */
export function useIsMobile() {
  return useSyncExternalStore(
    (cb) => {
      const m = mq();
      m?.addEventListener('change', cb);
      return () => m?.removeEventListener('change', cb);
    },
    isMobile,
    () => false,
  );
}

export type NavItem = { g: string } | { h: string; k: IconName; n: string; a?: string; c?: () => number | undefined };

/* ─── нижняя панель и шторка «Ещё» ─── */

const MAIN = ['/dashboard', '/calendar', '/clients'];
/** Пункт меню активен на своём разделе и его страницах-потомках (/clients/123 → /clients) */
const isOn = (path: string, h: string) => path === h || path.startsWith(h + '/');

const DOTS = (
  <svg viewBox="0 0 20 20" fill="currentColor">
    <circle cx="4.5" cy="10" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="15.5" cy="10" r="1.6" />
  </svg>
);

const CHAT = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M3.5 5.5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-3.5 3v-3h0a2 2 0 0 1-2-2z" />
  </svg>
);

export function MobileNav({ nav, ini, name }: { nav: NavItem[]; ini: string; name: string }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const items = nav.filter((x): x is Extract<NavItem, { h: string }> => 'h' in x);
  const main = MAIN.map((h) => items.find((x) => x.h === h)).filter(Boolean) as typeof items;
  const more = items.filter((x) => !MAIN.includes(x.h));
  const moreOn = !MAIN.some((h) => isOn(pathname, h)) && !isOn(pathname, '/constructor');

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.documentElement.classList.toggle('msh-lock', open);
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('keydown', k);
      document.documentElement.classList.remove('msh-lock');
    };
  }, [open]);

  /* Заголовок группы — только если под ним в шторке есть пункты */
  const sheet: ReactNode[] = [];
  nav.forEach((x, i) => {
    if ('g' in x) {
      const next = nav.slice(i + 1);
      const end = next.findIndex((y) => 'g' in y);
      if ((end < 0 ? next : next.slice(0, end)).some((y) => 'h' in y && more.includes(y)))
        sheet.push(
          <div className="msh-g" key={'g' + i}>
            {x.g}
          </div>,
        );
      return;
    }
    if (!more.includes(x)) return;
    const I = ICON[x.k];
    const c = x.c?.();
    sheet.push(
      <Link key={x.h} className={'msh-i' + (isOn(pathname, x.h) ? ' on' : '')} to={x.h}>
        <I />
        <span>{x.n}</span>
        {x.c && c != null && <s>{c}</s>}
      </Link>,
    );
  });

  return (
    <>
      <nav className="mtabs" aria-label="Основная навигация">
        {main.map((x) => {
          const I = ICON[x.k];
          return (
            <NavLink key={x.h} className={({ isActive }) => 'mt' + (isActive ? ' on' : '')} to={x.h} end={x.h === '/dashboard'}>
              <I />
              <span>{x.n}</span>
            </NavLink>
          );
        })}
        <button className={'mt' + (moreOn ? ' on' : '')} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}>
          {DOTS}
          <span>Ещё</span>
        </button>
      </nav>
      <div className={'msh-ov' + (open ? ' on' : '')} onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
        <div className="msh" role="dialog" aria-modal="true" aria-label="Разделы">
          <div className="msh-grab"></div>
          {sheet}
          <div className="msh-g">Аккаунт</div>
          {/* «Поддержка» в шторке скрыта — чат открывается кружком в углу (Support.tsx).
          {sup && (
            <a
              href="#"
              className="msh-i"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                sup.open();
              }}
            >
              <span className="msh-sup">{CHAT}</span>
              <span>Поддержка</span>
              {sup.dot && <span className="sdot" />}
            </a>
          )}
          */}
          <Link className={'msh-i' + (isOn(pathname, '/profile') ? ' on' : '')} to="/profile">
            <span className="msh-av">{ini}</span>
            <span>{name}</span>
          </Link>
        </div>
      </div>
    </>
  );
}

/* ─── «назад» в шапке страниц-потомков ─── */

const BACK: [RegExp, string][] = [
  [/^\/clients\/./, '/clients'],
  [/^\/exercises\/./, '/exercises'],
  [/^\/programs\/./, '/programs'],
  [/^\/constructor/, '/dashboard'],
];

/** Пришли из кабинета — назад по истории (в ленту, календарь, список), по прямой ссылке — в свой раздел */
export function MBack() {
  const loc = useLocation();
  const nav = useNavigate();
  const to = BACK.find(([re]) => re.test(loc.pathname))?.[1];
  if (!to) return null;
  return (
    <Link
      className="mback"
      to={to}
      aria-label="Назад"
      onClick={(e) => {
        if (loc.key !== 'default') {
          e.preventDefault();
          nav(-1);
        }
      }}
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 3 5 8l5 5" />
      </svg>
    </Link>
  );
}

/* ─── правые панели — шторкой снизу ─── */

/** Открыта ли шторка правой панели: класс body.mrail-on, Escape закрывает */
export function useMobileRail() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    document.body.classList.toggle('mrail-on', on);
    if (!on) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOn(false);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('keydown', k);
      document.body.classList.remove('mrail-on');
    };
  }, [on]);
  useEffect(() => () => document.body.classList.remove('mrail-on'), []);
  return [on, setOn] as const;
}

/** Шапка шторки внутри .rail и затемнение за ней */
export function MRailHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="mrail-x">
      <b>{title}</b>
      <button type="button" aria-label="Закрыть" onClick={onClose}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  );
}
export const MRailBackdrop = ({ onClose }: { onClose: () => void }) => <div className="mrail-bd" onClick={onClose} />;

/** Строка над днём в конструкторе: открывает шторку «Календарь и базы» */
export function MRailButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="mrail-btn" onClick={onClick}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2.5" y="4" width="15" height="13.5" rx="2" />
        <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" />
      </svg>
      <span>Календарь и базы шаблонов</span>
      <s>скопировать · добавить</s>
    </button>
  );
}

/* ─── списки ─── */

/** Шапки таблицы на телефоне не видно, а с ней и сортировки: выбор поля и направление одной строкой */
export function MSort({ cols, cur, dir, onSort }: { cols: { k: string; n: string }[]; cur: string | null; dir: number; onSort: (k: string) => void }) {
  if (!cols.length) return null;
  const has = !!cur && cols.some((c) => c.k === cur);
  return (
    <div className="msort">
      <label>
        <span>Сортировка</span>
        <select data-native aria-label="Сортировка" value={has ? cur! : ''} onChange={(e) => e.target.value && e.target.value !== cur && onSort(e.target.value)}>
          {!has && (
            <option value="" disabled>
              по умолчанию
            </option>
          )}
          {cols.map((c) => (
            <option key={c.k} value={c.k}>
              {c.n}
            </option>
          ))}
        </select>
      </label>
      <button type="button" aria-label="Направление сортировки" disabled={!has} onClick={() => has && onSort(cur!)}>
        {has && dir < 0 ? '↓' : '↑'}
      </button>
    </div>
  );
}

/** Ячейка с прочерком в карточке — пустая строка: прячем её (.mnil). Таблица «pm» — настоящая таблица, её не трогаем. */
export function useNilCells(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll<HTMLTableCellElement>('.tbl[data-tbl]:not([data-tbl="pm"]) td').forEach((td) => {
      const nil = /^[—–-]?$/.test((td.textContent || '').trim()) && !td.querySelector('img,svg,input,button');
      td.classList.toggle('mnil', nil);
    });
  });
}

/* ─── календарь: шапка прячется при листании вниз и возвращается при листании вверх ─── */

export function useSmartSticky(sel: string) {
  useEffect(() => {
    let last = scrollY;
    const on = () => {
      const h = document.querySelector(sel);
      if (!h) return;
      if (!isMobile()) return void h.classList.remove('mhid');
      const y = scrollY;
      const d = y - last;
      if (Math.abs(d) < 8) return;
      h.classList.toggle('mhid', d > 0 && y > 160);
      last = y;
    };
    addEventListener('scroll', on, { passive: true });
    return () => removeEventListener('scroll', on);
  }, [sel]);
}

/* ─── автофокус полей поиска ───
   Списки после перерисовки возвращают фокус в поле поиска (и autoFocus при открытии) — на телефоне это
   поднимало бы клавиатуру от тапа по фильтру. Пропускаем focus(), если в поле только что не печатали. */
let focusLock = false;
export function installFocusLock() {
  if (focusLock || typeof HTMLElement === 'undefined') return;
  focusLock = true;
  let typedAt = 0;
  addEventListener('input', () => (typedAt = Date.now()), true);
  const native = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (o?: FocusOptions) {
    if (isMobile() && this.matches?.('input#q, .pane-h input, .dsel-s input, #cl-q') && Date.now() - typedAt > 600) return;
    return native.call(this, o);
  };
}
